import {
  AnalyticsIcon,
  DocumentIcon,
  GuidesIcon,
  LaunchIcon,
  LearningIcon,
  MessageIcon,
  PlugIcon,
  ToolIcon,
  UnderstandingIcon,
  UserIcon,
} from '@segment/matcha/components/Icons/Brandbook'
import type { IconComponentType } from '@segment/matcha/components/Icons/utils/createIconsComponent'

export type ContentType = `article` | `course` | `guide` | `video` | `webinar`

export type Topic = `analytics` | `identity-resolution` | `onboarding-resources` | `tracking-best-practices`

export type Role = `analytics` | `engineering` | `marketing` | `product`

export interface HelpCardAttributes {
  contentTypes: ContentType[]
  topics: Topic[]
  roles: Role[]
}

export interface HelpCardRecord extends HelpCardAttributes {
  title: string
  description: string
  href: string
  icon: IconComponentType
}

const data: HelpCardRecord[] = [
  {
    title: `Technical Docs`,
    description: `Find the answers to any of your specific technical questions about all features of the Segment CDP platform.`,
    icon: DocumentIcon,
    href: `https://segment.com/docs/`,
    contentTypes: [`article`, `guide`],
    topics: [`identity-resolution`, `onboarding-resources`, `tracking-best-practices`],
    roles: [`engineering`, `marketing`, `product`],
  },
  {
    title: `Segment University`,
    description: `A hands-on video tutorial of how to use Segment, offering step-by-step instructions for: planning what to track, how to deploying tracking, configuring integrations, managing traits & audiences, and more.`,
    icon: LearningIcon,
    href: `https://university.segment.com/`,
    contentTypes: [`article`, `course`, `guide`, `video`],
    topics: [`analytics`, `identity-resolution`, `onboarding-resources`, `tracking-best-practices`],
    roles: [`analytics`, `engineering`, `marketing`, `product`],
  },
  {
    title: `Analytics Academy`,
    description: `Introduction to the wide world of analytics, including: how to choose metrics, a guide for choosing your stack (and how your stack should evolve), and how to build a data-driven company.`,
    icon: AnalyticsIcon,
    href: `/academy/`,
    contentTypes: [`article`, `course`, `guide`],
    topics: [`analytics`, `onboarding-resources`, `tracking-best-practices`],
    roles: [`analytics`, `engineering`, `marketing`, `product`],
  },
  {
    title: `Recipes`,
    description: `Looking for more ways to supercharge your stack with Segment and get more value from your data? Explore our cookbook of use-cases with step-by-step instructions of how to implement them.`,
    icon: GuidesIcon,
    href: `/recipes/`,
    contentTypes: [`article`, `guide`],
    topics: [`analytics`, `tracking-best-practices`],
    roles: [`analytics`, `engineering`, `marketing`, `product`],
  },
  {
    title: `Technical Support`,
    description: `Can't find your answer elsewhere? Submit a ticket with our Technical Support team.`,
    icon: ToolIcon,
    href: `https://segment.com/help/contact/`,
    contentTypes: [],
    topics: [`analytics`, `identity-resolution`, `onboarding-resources`, `tracking-best-practices`],
    roles: [`analytics`, `engineering`, `marketing`, `product`],
  },
  {
    title: `Demo`,
    description: `Connect with a Segment expert from our Sales team who can share more about the product and answer any questions you have.`,
    icon: MessageIcon,
    href: `/demo/`,
    contentTypes: [],
    topics: [`analytics`],
    roles: [`analytics`, `engineering`, `marketing`, `product`],
  },
  {
    title: `Events`,
    description: `Join Segment experts for industry insights and best practices advice, and check out recordings of past events.`,
    icon: UserIcon,
    href: `/events/`,
    contentTypes: [`video`, `webinar`],
    topics: [`analytics`, `tracking-best-practices`],
    roles: [`analytics`, `engineering`, `marketing`, `product`],
  },
  {
    title: `Blog`,
    description: `The Segment team's latest thinking on all things data, product, marketing, and growth.`,
    icon: UnderstandingIcon,
    href: `https://segment.com/blog/`,
    contentTypes: [],
    topics: [],
    roles: [],
  },
  {
    title: `Status`,
    description: `Follow live monitoring of Segment’s uptime and status updates on any identified performance issues.`,
    icon: PlugIcon,
    href: `https://status.segment.com/`,
    contentTypes: [],
    topics: [],
    roles: [],
  },
  {
    title: `Growth Masters`,
    description: `Learn how to build better products for your customers and grow your business. This collection of on-demand video lessons are led by industry thought leaders and deliver actionable recommendations on topics like user research, experimentation, onboarding, and more.`,
    icon: LaunchIcon,
    href: `https://growth.segment.com/`,
    contentTypes: [`course`, `video`],
    topics: [`analytics`],
    roles: [`analytics`, `engineering`, `marketing`, `product`],
  },
]

export default data
